/**
 * api.js — MarginPro frontend API layer
 *
 * All backend requests flow through here. To point at a deployed CSP
 * instance, update BASE_URL to your hosted API origin and the rest
 * of the app needs no changes.
 *
 * Endpoints matched to backend routes/auth.js & routes/users.js:
 *   POST  /api/v1/auth/register
 *   POST  /api/v1/auth/login
 *   GET   /api/v1/auth/me
 *   GET   /api/v1/users/profile
 *   PATCH /api/v1/users/profile
 */

// ─── Base URL ─────────────────────────────────────────────────────────────────
// Development: local backend on port 5983
// Production:  swap this to your deployed API URL, e.g. 'https://api.marginpro.io'
const BASE_URL = process.env.REACT_APP_API_URL || 'https://mp-app-ausfb.ondigitalocean.app';

// ─── Token helpers ────────────────────────────────────────────────────────────
export const tokenStore = {
  get: ()        => localStorage.getItem('mp_token'),
  set: (token)   => localStorage.setItem('mp_token', token),
  clear: ()      => localStorage.removeItem('mp_token'),
};

// ─── Core fetch wrapper ───────────────────────────────────────────────────────
/**
 * request(path, options)
 * Wraps fetch with:
 *  - JSON headers
 *  - Bearer token injection when a token exists
 *  - Consistent error shape: { success, message, data? }
 */
const request = async (path, options = {}) => {
  const token = tokenStore.get();

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const json = await res.json().catch(() => ({
    success: false,
    message: 'Server returned an unreadable response.',
  }));

  if (!res.ok) {
    // Normalise backend error shape into a thrown Error for callers to catch
    const err = new Error(json.message || `Request failed with status ${res.status}`);
    err.status = res.status;
    err.data = json;
    throw err;
  }

  return json;
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

/**
 * signup({ firstName, lastName, email, password })
 * Registers a new user. Stores the returned JWT automatically.
 * Returns the full API response: { success, token, user }
 */
export const signup = async ({ firstName, lastName, email, password }) => {
  const data = await request('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({ firstName, lastName, email, password }),
  });
  // Backend nests the token under data.data.token (see utils/response.js).
  const token = data.data?.token;
  if (token) tokenStore.set(token);
  return data;
};

/**
 * login({ email, password })
 * Authenticates an existing user. Stores the returned JWT automatically.
 * Returns the full API response: { success, token, user }
 */
export const login = async ({ email, password }) => {
  const data = await request('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  // Backend nests the token under data.data.token (see utils/response.js).
  const token = data.data?.token;
  if (token) tokenStore.set(token);
  return data;
};

/**
 * getMe()
 * Returns the authenticated user object from the token.
 * Requires a valid stored token.
 */
export const getMe = async () => {
  return request('/api/v1/auth/me');
};

/**
 * logout()
 * Clears the stored token client-side. No backend call needed.
 */
export const logout = () => {
  tokenStore.clear();
};

// ─── User ─────────────────────────────────────────────────────────────────────

/**
 * getProfile()
 * Fetches the full user profile. Requires auth.
 */
export const getProfile = async () => {
  return request('/api/v1/users/profile');
};

/**
 * updateProfile(updates)
 * Patches the user profile. Requires auth.
 * @param {Object} updates — partial profile fields to update
 */
export const updateProfile = async (updates) => {
  return request('/api/v1/users/profile', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
};

// ─── KPI Profile ────────────────────────────────────────────────────────────
// The 5 core KPIs (Gross Margin, Break-Even Point, ROI, Burn Rate / Cash
// Runway, Overhead Ratio) that MarginPro gathers to configure recommendations.

/**
 * getKpiProfile()
 * Fetches (or lazily creates) the current user's KPI profile.
 */
export const getKpiProfile = async () => {
  return request('/api/v1/kpis/profile');
};

/**
 * updateKpiProfile(sectionUpdates)
 * Patches one or more KPI sections. e.g.
 *   updateKpiProfile({ grossMargin: { revenue: 100000, cost: 40000 } })
 * Computed values + completion status are always derived server-side.
 */
export const updateKpiProfile = async (sectionUpdates) => {
  return request('/api/v1/kpis/profile', {
    method: 'PATCH',
    body: JSON.stringify(sectionUpdates),
  });
};

// ─── LLM Assistant — Chat Sessions ──────────────────────────────────────────
// The assistant is organized into multiple named threads ("chats"), each
// with its own persisted message history, mirroring the left-sidebar chat
// history list in the Chat workspace.
//
// ─── Client-side cache ──────────────────────────────────────────────────────
// Note on cost: none of the GET requests below ever call the Anthropic API —
// they're plain reads from our own backend/database (listing chats, loading
// a transcript). Only the two POST calls (sendSessionMessage, below, and
// generateMilestones) actually invoke the LLM and cost anything. Caching the
// GETs here doesn't save API spend, but it does cut down on redundant
// round-trips to our own server every time a user re-opens a chat or
// switches back to one they were just looking at.
//
// This is a simple in-memory, write-through cache: reads are served from it
// when available, and every mutation (send/create/delete/rename) patches it
// directly instead of invalidating and re-fetching. It lives for the
// lifetime of the page (cleared on a full reload) — there's no multi-tab or
// multi-device sync concern here since only this user's own actions change
// this data.
const chatCache = {
  sessions: null, // Array<Session> | null
  messagesBySession: new Map(), // sessionId -> { session, messages }
};

const cacheUpsertSession = (session) => {
  if (!chatCache.sessions) return;
  chatCache.sessions = [session, ...chatCache.sessions.filter((s) => s._id !== session._id)];
};

/**
 * getChatSessions({ force })
 * Lists the user's chat threads, most recently active first. Served from
 * cache after the first call unless `force` is set.
 */
export const getChatSessions = async ({ force = false } = {}) => {
  if (!force && chatCache.sessions) {
    return { success: true, message: 'Cached', data: { sessions: chatCache.sessions, count: chatCache.sessions.length } };
  }
  const res = await request('/api/v1/llm/sessions');
  chatCache.sessions = res.data.sessions;
  return res;
};

/**
 * createChatSession()
 * Starts a new, empty chat thread ("New Chat").
 */
export const createChatSession = async () => {
  const res = await request('/api/v1/llm/sessions', { method: 'POST' });
  cacheUpsertSession(res.data.session);
  chatCache.messagesBySession.set(res.data.session._id, { session: res.data.session, messages: [] });
  return res;
};

/**
 * getSessionMessages(sessionId, { force })
 * Returns the session + its full persisted transcript. Served from cache
 * after the first load of a given thread unless `force` is set.
 */
export const getSessionMessages = async (sessionId, { force = false } = {}) => {
  if (!force && chatCache.messagesBySession.has(sessionId)) {
    const cached = chatCache.messagesBySession.get(sessionId);
    return { success: true, message: 'Cached', data: { session: cached.session, messages: cached.messages } };
  }
  const res = await request(`/api/v1/llm/sessions/${sessionId}/messages`);
  chatCache.messagesBySession.set(sessionId, { session: res.data.session, messages: res.data.messages });
  return res;
};

/**
 * sendSessionMessage(sessionId, message)
 * Sends one message within a specific thread — this is a real LLM call and
 * is never cached. Returns { session, userMessage, assistantMessage }, and
 * patches the cache so the next time this thread or the session list is
 * read, no extra round-trip is needed.
 */
export const sendSessionMessage = async (sessionId, message) => {
  const res = await request(`/api/v1/llm/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

  const prior = chatCache.messagesBySession.get(sessionId)?.messages || [];
  chatCache.messagesBySession.set(sessionId, {
    session: res.data.session,
    messages: [...prior, res.data.userMessage, res.data.assistantMessage],
  });
  cacheUpsertSession(res.data.session);

  return res;
};

/**
 * renameChatSession(sessionId, title)
 */
export const renameChatSession = async (sessionId, title) => {
  const res = await request(`/api/v1/llm/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
  cacheUpsertSession(res.data.session);
  const cached = chatCache.messagesBySession.get(sessionId);
  if (cached) chatCache.messagesBySession.set(sessionId, { ...cached, session: res.data.session });
  return res;
};

/**
 * deleteChatSession(sessionId)
 */
export const deleteChatSession = async (sessionId) => {
  const res = await request(`/api/v1/llm/sessions/${sessionId}`, { method: 'DELETE' });
  if (chatCache.sessions) {
    chatCache.sessions = chatCache.sessions.filter((s) => s._id !== sessionId);
  }
  chatCache.messagesBySession.delete(sessionId);
  return res;
};

/**
 * generateMilestones()
 * Asks the assistant to propose milestones grounded in the user's KPI
 * profile + chat history. Persists and returns the created milestones.
 */
export const generateMilestones = async () => {
  return request('/api/v1/llm/milestones', { method: 'POST' });
};

// ─── Projects ─────────────────────────────────────────────────────────────
// Projects group chats and carry shared context: a server-detected
// persona blend, a persona-weighted KPI set (computed server-side — zero
// LLM tokens), and a capital risk read. See backend routes/projects.js.

/**
 * getIntakeQuestions()
 * The persona-detection questionnaire shown when creating a project.
 * Server-defined — answers are scored server-side, no LLM involved.
 */
export const getIntakeQuestions = async () => {
  return request('/api/v1/projects/intake-questions');
};

/** listProjects() — all of the user's projects, most recently active first. */
export const listProjects = async () => {
  return request('/api/v1/projects');
};

/** createProject({ name, description }) — creates the project shell. */
export const createProject = async ({ name, description }) => {
  return request('/api/v1/projects', {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
};

/** getProject(projectId) — full project incl. computed KPIs, risk, descriptors. */
export const getProject = async (projectId) => {
  return request(`/api/v1/projects/${projectId}`);
};

/**
 * updateProject(projectId, updates)
 * Patch name/description/status/capital/kpiInputs. All computed values
 * (KPIs, risk) come back server-derived.
 */
export const updateProject = async (projectId, updates) => {
  return request(`/api/v1/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
};

/** deleteProject(projectId) — chats are detached (kept), not deleted. */
export const deleteProject = async (projectId) => {
  return request(`/api/v1/projects/${projectId}`, { method: 'DELETE' });
};

/**
 * submitProjectIntake(projectId, { answers, capital, kpiInputs })
 * Submits the initial questionnaire. The SERVER scores the persona,
 * computes the KPI set + risk, and generates the initial results chat —
 * no LLM call, no token spend.
 */
export const submitProjectIntake = async (projectId, payload) => {
  return request(`/api/v1/projects/${projectId}/intake`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

/** listProjectSessions(projectId) — chats belonging to a project. */
export const listProjectSessions = async (projectId) => {
  return request(`/api/v1/projects/${projectId}/sessions`);
};

/** createProjectSession(projectId) — start a new chat inside a project. */
export const createProjectSession = async (projectId) => {
  return request(`/api/v1/projects/${projectId}/sessions`, { method: 'POST' });
};

/**
 * assignSessionToProject(projectId, sessionId, { detach })
 * Attach an existing standalone chat to a project (or detach it).
 */
export const assignSessionToProject = async (projectId, sessionId, { detach = false } = {}) => {
  return request(`/api/v1/projects/${projectId}/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ detach }),
  });
};

// ─── Milestones ─────────────────────────────────────────────────────────

export const getMilestones = async () => {
  return request('/api/v1/milestones');
};

export const createMilestone = async (milestone) => {
  return request('/api/v1/milestones', {
    method: 'POST',
    body: JSON.stringify(milestone),
  });
};

export const updateMilestone = async (id, updates) => {
  return request(`/api/v1/milestones/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
};

export const deleteMilestone = async (id) => {
  return request(`/api/v1/milestones/${id}`, { method: 'DELETE' });
};
